import { Form, Formik, useFormikContext } from 'formik';
import { isEmpty, uniqueId, debounce, omit } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as LinkIcon } from 'lucide-react';
import Tooltip from '@dashboard/components/Tooltip';
import * as Yup from 'yup';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { currencyList, getMinimumChargeAmount } from '@dashboard/services/currency';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import { formatPriceInput } from '@dashboard/services/formatting';
import { formatPriceForInput } from '@dashboard/services/formatting/price';
import getProductType, {
  getProductValuesFromType,
  PRODUCT_TYPES,
} from '@dashboard/services/getProductType';

import usePage from '@dashboard/hooks/usePage';
import useScopedProductSubmit from '../useScopedProductSubmit';
import { PRICE_PICKER_KEYS, toProductPayload } from '../lib/productPayload';
import draftToPricePayload from '../lib/draftToPricePayload';
import { fromStartDateInput, toStartDateInput } from '../lib/startDateInput';
import { getPriceBillingConfig } from '@repo/checkout';

import { FormElement, Label } from '@dashboard/components/Form';
import Link from '@dashboard/components/Link';
import Spinner from '@dashboard/components/Spinner';
import { TextInput } from '@dashboard/components/Formik';
import FormError, { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';

import { getBillingPeriod } from '../../../utils/getBillingPeriod';
import { getTrial } from '../../../utils/getTrial';
import useProduct from '@/dashboard/scenes/PageEdit/scenes/PageProduct/useProduct';
import PricesList, { DEFAULT_NEW_PRICE } from './PricesList/PricesList';
import { applyTemplate } from './PricesList/quickStartTemplates';
import {
  reconcileCurrencyToSingle,
  fillMissingPriceReferences,
  normalizeRevealFlags,
  isBlankOrZeroFixedPrice,
} from './PricesList/priceHelpers';
import { confirm } from '@dashboard/lib/confirm';
import { synthesizePriceFromLegacy } from '../lib/synthesizePriceFromLegacy';
import {
  isVariantsPriced,
  toVariantsPricedPrice,
  PricedWithVariantsSection,
  VariantsPricingLink,
  BasePricingLink,
} from './PricedWithVariants';
import { UnsavedChangesReporter } from '@dashboard/components/UnsavedChanges';

// TODO(M14): The env-var rollout-window gate was removed in this commit; finish dropping
// the legacy product schema fields (price, pricingType, interval, ...) per the M14 plan.

const billingPeriods = [
  { name: 'day', label: 'Daily' },
  {
    name: 'week',
    label: 'Weekly',
  },
  {
    name: 'month',
    label: 'Monthly',
  },
  {
    name: 'quarter',
    label: 'Every 3 months',
  },
  {
    name: 'semiannual',
    label: 'Every 6 months',
  },
  {
    name: 'year',
    label: 'Yearly',
  },
];

Yup.setLocale({
  mixed: {
    required: ({ path }) => `${path} is required`,
  },
});

const productSchema = Yup.object().shape({
  title: Yup.string().min(3, '3 characters minimum').required('Product title is required'),
  type: Yup.mixed().oneOf(Object.values(PRODUCT_TYPES)).required(),
  pricingType: Yup.mixed().oneOf(['single', 'multiple']),
  currency: Yup.mixed().oneOf(Object.keys(currencyList)).required(),
  price: Yup.number().when(['type'], {
    is: type => type !== PRODUCT_TYPES.SUBSCRIPTION && type !== PRODUCT_TYPES.PAYMENT_PLAN,
    then: Yup.number().test('min', `min`, (price, values) => {
      if (!price || price === '0') {
        return true;
      }

      return (
        formatPriceInput(price, values.parent.currency) >=
        getMinimumChargeAmount(values.parent.currency)
      );
    }),
  }),
  setupFee: Yup.number()
    .nullable()
    .when(['type'], {
      is: type =>
        type === PRODUCT_TYPES.ONE_TIME ||
        type === PRODUCT_TYPES.PWYW ||
        type === PRODUCT_TYPES.SUBSCRIPTION ||
        type === PRODUCT_TYPES.PAYMENT_PLAN,
      then: Yup.number().test('min', `min`, (price, values) => {
        if (!price || price === '0') {
          return true;
        }

        return (
          formatPriceInput(price, values.parent.currency) >=
          getMinimumChargeAmount(values.parent.currency)
        );
      }),
    }),
  setupFeeMultipliesWithQuantity: Yup.boolean().nullable(),
  payWhatYouWant: Yup.boolean().nullable(),
  free: Yup.boolean().nullable(),
  billingPeriod: Yup.string().oneOf(billingPeriods.map(bp => bp.name)),
  trialPeriodDays: Yup.number()
    .nullable()
    .when(['type'], {
      is: type => type === PRODUCT_TYPES.SUBSCRIPTION || type === PRODUCT_TYPES.PAYMENT_PLAN,
      then: Yup.number().min(0, '0 days minimum').nullable(),
    }),
  billingCycleAnchorConfig: Yup.object().shape({
    enabled: Yup.boolean(),
    dayOfMonth: Yup.number().nullable(),
    month: Yup.number().nullable(),
  }),
  // The product-level `planIterations` scalar is legacy: in the multi-price model
  // the number of payments lives per-price and is validated by PriceForm + the API
  // `Price` schema. `getProductType` derives `type` as PAYMENT_PLAN from the first
  // price, but the product scalar stays null for multi-price products — so requiring
  // it here silently failed submitForm() (no FormError is bound to it) and sent no
  // PATCH. Only require it for a legacy product with no prices[] rows.
  planIterations: Yup.number().when(['type', 'prices'], {
    is: (type, prices) =>
      type === PRODUCT_TYPES.PAYMENT_PLAN && !(Array.isArray(prices) && prices.length > 0),
    then: Yup.number()
      .required('No. of payments is required')
      .min(1, 'No. of payments should be empty or at least 2 payments')
      // make sure that planIterations cant go over 20 years
      .when(['billingPeriod'], {
        is: 'day',
        then: Yup.number()
          .nullable()
          .typeError('No. of payments is required')
          .max(7300, 'No. of payments should be less than 20 years'),
      })
      .when(['billingPeriod'], {
        is: 'week',
        then: Yup.number()
          .nullable()
          .typeError('No. of payments is required')
          .max(1040, 'No. of payments should be less than 20 years'),
      })
      .when(['billingPeriod'], {
        is: 'month',
        then: Yup.number()
          .nullable()
          .typeError('No. of payments is required')
          .max(240, 'No. of payments should be less than 20 years'),
      })
      .when(['billingPeriod'], {
        is: 'quarter',
        then: Yup.number()
          .nullable()
          .typeError('No. of payments is required')
          .max(80, 'No. of payments should be less than 20 years'),
      })
      .when(['billingPeriod'], {
        is: 'semiannual',
        then: Yup.number()
          .nullable()
          .typeError('No. of payments is required')
          .max(40, 'No. of payments should be less than 20 years'),
      })
      .when(['billingPeriod'], {
        is: 'year',
        then: Yup.number()
          .nullable()
          .typeError('No. of payments is required')
          .max(20, 'No. of payments should be less than 20 years'),
      }),
    otherwise: Yup.number().nullable(),
  }),
});

function getDefaultProductValues(seller) {
  const defaultCurrency = seller.defaultCurrency || 'usd';
  const initialPriceKey = uniqueId('price-');
  return {
    type: 'charge',
    title: '',
    pricingType: 'single',
    currency: defaultCurrency,
    prices: [
      {
        billingType: 'one_time',
        __pricing: 'fixed',
        __schedule: 'subscription',
        __trial: 'none',
        __discountOpen: false,
        __startDateOpen: false,
        __setupFeeOpen: false,
        label: '',
        description: '',
        badge: '',
        badgeIcon: null,
        reference: '',
        amount: '',
        currency: defaultCurrency,
        enabled: true,
        hasBeenPurchased: false,
        payWhatYouWant: false,
        pwywSuggestedPrice: '',
        discountedFromPrice: '',
        setupFee: '',
        setupFeeMultipliesWithQuantity: false,
        interval: 'month',
        intervalCount: 1,
        trialPeriodDays: '',
        planIterations: '',
        billingCycleAnchorConfig: { enabled: false, dayOfMonth: 1, month: '' },
        key: initialPriceKey,
      },
    ],
    __draftPriceId: initialPriceKey,
    defaultPriceId: null,
    priceDisplayLabels: {},
    priceDisplayDefaults: {},
    priceDisplayTabOrder: {},
    price: '',
    discountedFromPrice: '',
    payWhatYouWant: false,
    trial: '',
    trialPeriodDays: '',
    billingCycleAnchorConfig: {
      enabled: false,
      dayOfMonth: 1,
      month: null,
    },
    planIterations: null,
    setupFee: '',
    setupFeeMultipliesWithQuantity: false,
    startDate: null,
    media: [],
    billingPeriod: 'month',
    taxBehavior: '',
    taxCode: '',
  };
}

function getFormValues(product, seller = {}) {
  if (isEmpty(product)) {
    return getDefaultProductValues(seller);
  } else {
    // TODO(M14): Delete the synthesizePriceFromLegacy call once the backfill migration
    // (2026-05-26-multi-prices-per-product) is verified in production and every product
    // has a non-empty prices[] array. See
    // docs/superpowers/specs/2026-05-26-multi-prices-per-product-design.md
    // section "Migration" + the runbook in
    // apps/api/migrations/2026-05-26-multi-prices-per-product.runbook.md
    const rawPrices = Array.isArray(product.prices) ? product.prices : [];
    // Render by the `order` field — the source of truth the storefront sorts
    // by — not raw array position. A save can return prices whose array order
    // diverges from their `order` field (Mongoose DocumentArray doesn't
    // reliably persist a re-sorted array), which would otherwise revert a
    // drag-reorder on the next read. Stable sort preserves array order on ties.
    const sortedPrices = [...rawPrices].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const basePrices =
      sortedPrices.length > 0
        ? sortedPrices.map(p => ({
            ...p,
            description: p.description ?? '',
            badge: p.badge ?? '',
            badgeIcon: p.badgeIcon ?? null,
            reference: p.reference ?? '',
            amount:
              p.amount != null
                ? formatPriceForInput({
                    amount: p.amount,
                    currency: p.currency || product.currency,
                    style: 'decimal',
                  })
                : '',
            discountedFromPrice: p.discountedFromPrice
              ? formatPriceForInput({
                  amount: p.discountedFromPrice,
                  currency: p.currency || product.currency,
                  style: 'decimal',
                })
              : '',
            setupFee: p.setupFee
              ? formatPriceForInput({
                  amount: p.setupFee,
                  currency: p.currency || product.currency,
                  style: 'decimal',
                })
              : '',
            pwywSuggestedPrice: p.pwywSuggestedPrice
              ? formatPriceForInput({
                  amount: p.pwywSuggestedPrice,
                  currency: p.currency || product.currency,
                  style: 'decimal',
                })
              : '',
          }))
        : [synthesizePriceFromLegacy(product)];
    // Flatten the price's billing config into the flat draft fields the form
    // binds (the Formik tree stays flat by design — spec §7 in
    // tasks/nest-price-billing-config-spec.md). getPriceBillingConfig hides
    // whether the saved price is nested (post-cutover), flat (pre-migration),
    // or the synthesized legacy entry. The saved recurring/paymentPlan objects
    // still ride the ...p spread; draftToNestedPrice discards them at submit.
    // Also seeds the UI-only RadioCard selections + tick-to-reveal flags here
    // (not via an effect) so opening a product doesn't mark the form dirty.
    const prices = basePrices.map(p => {
      const config = getPriceBillingConfig(p) || {};
      return {
        ...p,
        interval: config.interval,
        intervalCount: config.intervalCount,
        trialPeriodDays: config.trialPeriodDays ?? '',
        planIterations: config.planIterations ?? '',
        billingCycleAnchorConfig: config.billingCycleAnchorConfig
          ? {
              enabled: !!config.billingCycleAnchorConfig.enabled,
              dayOfMonth: config.billingCycleAnchorConfig.dayOfMonth ?? 1,
              month: config.billingCycleAnchorConfig.month ?? '',
            }
          : { enabled: false, dayOfMonth: 1, month: '' },
        // Format to the datetime-local input format (yyyy-MM-ddTHH:mm, UTC) —
        // a datetime-local input can't display a raw ISO string, so a saved
        // subscription start date would render empty.
        startDate: config.startDate ? toStartDateInput(config.startDate) : '',
        __pricing: p.payWhatYouWant ? 'pwyw' : 'fixed',
        __schedule: config.planIterations > 0 ? 'payment-plan' : 'subscription',
        __trial: config.trialPeriodDays > 0 ? (p.setupFee ? 'paid' : 'free') : 'none',
        __discountOpen: !!p.discountedFromPrice,
        __startDateOpen: !!config.startDate,
        __setupFeeOpen: !!p.setupFee,
      };
    });

    return {
      type: getProductType(product),
      title: product.title,
      currency: product.currency,
      pricingType: product.pricingType || 'single',
      prices,
      __draftPriceId: null,
      defaultPriceId: product.defaultPriceId || null,
      priceDisplayLabels: product.pricePicker?.priceDisplayLabels || {},
      priceDisplayDefaults: product.pricePicker?.priceDisplayDefaults || {},
      priceDisplayTabOrder: product.pricePicker?.priceDisplayTabOrder || {},
      pricePickerTitle: product.pricePicker?.title || '',
      pricePickerDescription: product.pricePicker?.description || '',
      shortenDescription: product.pricePicker?.shortenDescription !== false,
      // TODO(M14): edits legacy product scalars for pre-migration products — remove with the legacy fields after the backfill migration
      // (covers currency above and price/discountedFromPrice/payWhatYouWant/planId/productId/trial*/billingCycleAnchorConfig/
      // planIterations/billingPeriod/setupFee*/startDate/planManagedInStripe below)
      price: formatPriceForInput({
        amount: product.price,
        currency: product.currency,
        style: 'decimal',
      }),
      discountedFromPrice: product.discountedFromPrice
        ? formatPriceForInput({
            amount: product.discountedFromPrice,
            currency: product.currency,
            style: 'decimal',
          })
        : '',
      payWhatYouWant: product.payWhatYouWant,
      media: product.media || [],
      planId: product.planId,
      productId: product.productId,
      trial: getTrial(product),
      trialPeriodDays: product.trialPeriodDays,
      billingCycleAnchorConfig: {
        enabled: product.billingCycleAnchorConfig?.enabled,
        dayOfMonth: product.billingCycleAnchorConfig?.dayOfMonth || 1,
        month: product.billingCycleAnchorConfig?.month || null,
      },
      planIterations: product.planIterations,
      billingPeriod: getBillingPeriod(product.interval, product.intervalCount),
      setupFee: product.setupFee
        ? formatPriceForInput({
            amount: product.setupFee,
            currency: product.currency,
            style: 'decimal',
          })
        : '',
      setupFeeMultipliesWithQuantity: product.setupFeeMultipliesWithQuantity,
      startDate: product.startDate ? toStartDateInput(product.startDate) : null,
      planManagedInStripe: product.planManagedInStripe,
      taxBehavior: product.taxBehavior,
      taxCode: product.taxCode,
    };
  }
}

export const getFormValuesForTest = getFormValues;

// The flat price draft seed, shared with the order-bump product editor so it
// doesn't fork this mapping.
export { getFormValues as getProductFormValues };

/**
 * Prepare prices[] for submission: drop an empty draft when other prices exist,
 * and strip the __draftPriceId field from the values object.
 * Exported for unit testing.
 */
/**
 * A price counts as "configured" if it's pay-what-you-want or has an amount
 * entered. Used to block submission of a checkout with no real price (the
 * always-present draft form otherwise lets an empty price slip through).
 * Exported for unit testing.
 */
export function hasConfiguredPrice(prices) {
  return (
    Array.isArray(prices) &&
    prices.some(p => !!p.payWhatYouWant || (p.amount != null && String(p.amount).trim() !== ''))
  );
}

export function prepareSubmitValues(formValues, { isNew = true, editingSnapshot = null } = {}) {
  const { __draftPriceId, ...rest } = formValues;
  const prices = Array.isArray(rest.prices) ? rest.prices : [];

  const draftId = __draftPriceId;
  const priceId = p => p._id || p.key;
  const draftIdx = draftId != null ? prices.findIndex(p => priceId(p) === draftId) : -1;
  const draft = draftIdx >= 0 ? prices[draftIdx] : null;
  const otherPrices = prices.filter((_, i) => i !== draftIdx);

  let finalPrices = prices;
  if (!isNew && draft) {
    // Edit flow: the draft row NEVER rides a full product submit, dirty or not —
    // it persists only through its own Save (which clears __draftPriceId first).
    // Without this, any save triggered while a draft is open (delete, disable,
    // duplicate, …) would commit the half-finished draft as a side effect.
    finalPrices = otherPrices;
  } else if (draft && otherPrices.length >= 1) {
    // Create flow: committed prices exist — the draft form is only a proposal.
    // A price joins the checkout by clicking "Add another price", never by just
    // filling the form; filled or empty, the trailing draft is dropped here.
    finalPrices = otherPrices;
  }

  // Edit flow: a committed price open for editing submits its committed
  // snapshot, not its in-flight form values — unsaved edits persist only
  // through the card's own Save (which refreshes the snapshot first, making
  // this substitution a no-op). `enabled` and `order` ride along from the live
  // row: both are action-owned (kebab toggle / drag-reorder), never inline
  // edits, and the triggering action may have just changed them.
  if (!isNew && editingSnapshot) {
    const editedId = String(priceId(editingSnapshot));
    finalPrices = finalPrices.map(p =>
      String(priceId(p)) === editedId
        ? { ...editingSnapshot, enabled: p.enabled, order: p.order }
        : p,
    );
  }

  // On creation, a lone PWYW price uses its label as the checkout heading. The
  // create flow hides the Label field, so there's no way to name it during
  // creation — default it here when empty. Create-only and single-only: editing
  // never auto-fills, so a cleared label stays cleared (the checkout reads
  // price.label with no fallback).
  if (isNew && finalPrices.length === 1 && finalPrices[0].payWhatYouWant && !finalPrices[0].label) {
    finalPrices = [{ ...finalPrices[0], label: 'Pay what you want' }];
  }

  // Create flow: the lone DRAFT left with no real amount (blank or 0) isn't a
  // "free" single price — it means "no base price". Transform to pricingType
  // 'multiple' with the price zeroed so the checkout is created in the
  // no-base-price (variants) mode. Only the untouched draft transforms — an
  // ADDED price edited to 0 is an error, not a no-base-price signal, so it is
  // blocked (in submitProduct) instead. The edit flow keeps a lone 0 as a
  // single $0 price.
  let pricingType = rest.pricingType;
  if (
    isNew &&
    finalPrices.length === 1 &&
    draftId != null &&
    priceId(finalPrices[0]) === draftId &&
    isBlankOrZeroFixedPrice(finalPrices[0])
  ) {
    finalPrices = [{ ...finalPrices[0], amount: '0' }];
    pricingType = 'multiple';
  }

  return { ...rest, pricingType, prices: finalPrices };
}

/**
 * Merge in-flight price-form state back into a post-save reseed, so an action
 * save (disable / delete / duplicate / preselect / reorder) doesn't wipe the
 * open form: the add-price draft row is re-appended, and the edited price's
 * unsaved field edits are overlaid onto the server row (the server wins for
 * the action-owned `enabled`/`order`). The caller applies the result via
 * setFieldValue AFTER resetForm, so initialValues stay = server state and the
 * unsaved edits still register as dirty.
 * Returns null when nothing is in flight. Exported for unit testing.
 */
export function mergeInFlightPriceState(serverValues, formValues, editingSnapshot) {
  const priceId = p => p?._id || p?.key;
  const formPrices = Array.isArray(formValues.prices) ? formValues.prices : [];

  const draftId = formValues.__draftPriceId ?? null;
  const draftRow = draftId != null ? formPrices.find(p => priceId(p) === draftId) : null;

  const editedId = editingSnapshot != null ? String(priceId(editingSnapshot)) : null;
  const liveEdited =
    editedId != null ? formPrices.find(p => String(priceId(p)) === editedId) : null;

  let prices = Array.isArray(serverValues.prices) ? serverValues.prices : [];
  let changed = false;

  if (liveEdited) {
    prices = prices.map(serverRow => {
      if (String(priceId(serverRow)) !== editedId) return serverRow;
      changed = true;
      return { ...liveEdited, enabled: serverRow.enabled, order: serverRow.order };
    });
  }

  if (draftRow) {
    prices = [...prices, draftRow];
    changed = true;
  }

  if (!changed) return null;
  return { prices, draftId: draftRow ? draftId : null };
}

/**
 * Map a price-scoped API error message back to the price it refers to so it can
 * be shown inline. apps/api validatePriceFields prefixes the message with the
 * seller's label (`"Monthly": …`) or the price's 1-based position (`Price 3: …`).
 * Returns the 0-based index into `prices`, or null when not price-scoped.
 * Exported for unit testing.
 */
export function resolvePriceErrorIndex(message, prices) {
  if (!message || !Array.isArray(prices)) return null;
  const byPosition = /^Price (\d+):/.exec(message);
  if (byPosition) {
    const idx = Number(byPosition[1]) - 1;
    return idx >= 0 && idx < prices.length ? idx : null;
  }
  const byLabel = /^"(.+?)":/.exec(message);
  if (byLabel) {
    const idx = prices.findIndex(p => (p.label || '') === byLabel[1]);
    return idx >= 0 ? idx : null;
  }
  return null;
}

/** Strip the `"Label": ` / `Price N: ` prefix so the message reads cleanly inline. */
export function stripPriceErrorPrefix(message) {
  return (message || '').replace(/^(?:Price \d+|"[^"]+"):\s*/, '');
}

/**
 * Product title field. In multi-price edit mode there's no form-level Save
 * button, so the title autosaves (debounced) as you type; prices persist via
 * their own actions/Save. In single-price edit mode (`autosave={false}`) the
 * title is saved by the single-price form's Save button alongside the price, so
 * it must NOT autosave. On create it just edits Formik state until "Create
 * checkout".
 */
function TitleAutosaveField({ isNew, autosave = true }) {
  // Title-only payload: the autosave can never commit or clobber price rows
  // (an open draft / a price being edited) regardless of timing — the server
  // ignores pricing when the payload has no `prices` key.
  const submitScoped = useScopedProductSubmit();
  const submitRef = useRef(submitScoped);
  submitRef.current = submitScoped;
  const debouncedSave = useMemo(
    () =>
      debounce(() => {
        if (!isNew && autosave) submitRef.current(['title']);
      }, 800),
    [isNew, autosave],
  );
  // The unsaved-changes guard lets you leave mid-debounce, so the pending save still goes out.
  useEffect(() => () => debouncedSave.flush(), [debouncedSave]);
  return (
    <TextInput
      name="title"
      id="title"
      type="text"
      placeholder="Product title"
      autoFocus={!!isNew}
      onChange={() => debouncedSave()}
    />
  );
}

// `isNew` must default to an explicit false: the edit page (PageProduct)
// mounts this form without the prop, and an undefined isNew forwarded as
// `prepareSubmitValues(values, { isNew })` would hit that option's
// `isNew = true` default — silently running the edit page as the create
// flow and skipping the draft-exclusion + editing-snapshot substitution.
const ProductDetailsForm = ({ isNew = false, onChange }) => {
  const { seller } = useAuth();
  const { page, isLoading } = usePage();
  const { product, updateProduct } = useProduct();
  // Which committed price (if any) is currently expanded for editing. Mirrored
  // from PricesList's PriceEditContext so the create flow can block submission
  // while a price is mid-edit.
  const [editingPriceId, setEditingPriceId] = useState(null);
  // Committed snapshot of the price whose form is open (written synchronously
  // by PriceCard via PriceEditContext). Product submits substitute it for the
  // in-flight row so no action ever persists the open form's unsaved values.
  const editingSnapshotRef = useRef(null);
  // Bumped on every save; only the latest save is allowed to reseed the form via
  // resetForm, so a slow/superseded save's response can't revert newer edits
  // (e.g. a drag-reorder landing under a stale in-flight save).
  const saveSeqRef = useRef(0);

  async function submitProduct(formValues, actions) {
    actions.setStatus(undefined);

    try {
      const priceValues = getProductValuesFromType(formValues.type);
      const values = Object.assign(
        {},
        prepareSubmitValues(formValues, { isNew, editingSnapshot: editingSnapshotRef.current }),
        priceValues,
      );

      // Require at least one configured price to CREATE a checkout — the Price
      // field is marked required, but the always-present draft form would
      // otherwise let an empty price through. The edit flow is exempt: a saved
      // product may hold an empty price (just a billing type, no amount), so
      // clearing the lone amount is a legitimate save, not a blocked state.
      if (isNew && !hasConfiguredPrice(values.prices)) {
        toast({ title: 'Add at least one price before creating the checkout', type: 'error' });
        return;
      }

      // Create flow: every ADDED price must be > 0. The untouched draft already
      // transformed to a no-base-price checkout (pricingType 'multiple' + one
      // zeroed price) in prepareSubmitValues; anything else with a fixed 0/blank
      // price is blocked. The offending price already shows a live "Price must
      // be more than 0" error (its card + open form via AmountBlockedError), so
      // just prevent the save.
      if (isNew) {
        const isNoBasePrice =
          values.pricingType === 'multiple' && (values.prices || []).length === 1;
        if (!isNoBasePrice && (values.prices || []).some(isBlankOrZeroFixedPrice)) {
          return;
        }
      }

      // Reconcile the currency before persisting. A deferred per-price change (e.g.
      // a newly-added price set to a different currency) hasn't propagated yet, and
      // the create flow's draft has no per-price Save to run reconcileCurrencyOnSave.
      // Adopt the differing currency — confirming on a multi-price product — so every
      // price + the product share one (Stripe requires it; the API rejects a mismatch).
      const reconciled = await reconcileCurrencyToSingle({ values, confirm });
      if (!reconciled) return;
      values.currency = reconciled.currency;
      values.prices = reconciled.prices;

      if (values.pricingType === 'multiple' && !values.planManagedInStripe) {
        values.price = 0;
      }

      if (values.price !== 0) {
        values.price = formatPriceInput(values.price || 0, values.currency);
      }

      if (values.discountedFromPrice) {
        values.discountedFromPrice = formatPriceInput(
          values.discountedFromPrice || 0,
          values.currency,
        );
      }

      if (values.setupFee) {
        values.setupFee = formatPriceInput(values.setupFee || 0, values.currency);
      }

      if (Array.isArray(values.prices) && values.prices.length > 0) {
        values.prices = values.prices.map(p => draftToPricePayload(p, values.currency));

        // defaultPriceId may be a temporary key (e.g. "price-3") for new prices.
        // ProductService.update resolves temp keys to the matched price's ObjectId.
      }

      if (values.startDate) {
        values.startDate = fromStartDateInput(values.startDate);
      }

      if (values.billingCycleAnchorConfig.enabled) {
        if (values.billingPeriod === 'day' || values.billingPeriod === 'week') {
          values.billingCycleAnchorConfig.enabled = false;
        }
      }

      if (values.trialPeriodDays === 0) {
        values.trialPeriodDays = '';
        values.trial = '';
      }

      const saveSeq = ++saveSeqRef.current;
      const { data, error } = await updateProduct.mutateAsync({
        // The prices Settings tab saves these itself, so the form's copy is stale.
        ...toProductPayload(isNew ? values : omit(values, PRICE_PICKER_KEYS)),
        isNew,
      });

      if (data) {
        if (onChange) {
          // Signal the variants tab only when the merchant EXPLICITLY chose "no
          // base price" (pricingType was already 'multiple' in the form via "Use
          // variants instead"). A lone blank/0 draft that prepareSubmitValues
          // turns into a no-base-price product is not an explicit choice, so it
          // must not redirect to variants.
          onChange(data, { variantsPriced: isVariantsPriced(formValues) });
        }

        if (isNew) {
          // onChange triggers navigation; keep the submission pending so
          // the submit button stays disabled with its spinner until the
          // component unmounts, instead of briefly re-enabling first.
          return new Promise(() => {});
        }

        actions.setStatus('');
        // Skip reseeding if a newer save has started since this one — otherwise a
        // slow, superseded response would revert the user's newer edits (e.g. a reorder).
        if (saveSeq === saveSeqRef.current) {
          const serverValues = getFormValues(data, seller);
          // An action save while a price form is open must not wipe the open
          // form: re-apply the in-flight rows on top of the reseed. resetForm
          // first (initialValues = server state), then overlay via
          // setFieldValue so the unsaved edits still read as dirty.
          const inFlight = mergeInFlightPriceState(
            serverValues,
            formValues,
            editingSnapshotRef.current,
          );
          actions.resetForm({ values: serverValues });
          if (inFlight) {
            actions.setFieldValue('prices', inFlight.prices, false);
            actions.setFieldValue('__draftPriceId', inFlight.draftId, false);
          }
        }
        // Autosaves persist silently — the dashboard doesn't surface a "Saved"
        // toast for inline edits, so we don't either.
      } else {
        const { status, errors } = transformApiErrorToFormik(error);

        if (!isEmpty(errors)) {
          actions.setErrors(errors);
        } else {
          // A price-scoped message ("Price 2: …" / "\"Monthly\": …") is shown inline
          // on that price; anything else falls back to the form-level status.
          const priceIdx = resolvePriceErrorIndex(status, formValues.prices);
          if (priceIdx != null) {
            actions.setFieldError(`prices.${priceIdx}._apiError`, stripPriceErrorPrefix(status));
          } else {
            actions.setStatus(status);
          }
        }
      }
    } catch (error) {
      if (error && typeof error.message === 'string') {
        actions.setStatus(error.message);
      } else {
        actions.setStatus('Unable to save. Please contact support');
      }
    }
  }

  const initialValues = useMemo(() => getFormValues(product, seller), [product, seller]);

  /*   const showTaxSettings =
    seller?.tax?.enabled &&
    product.type === 'subscription' &&
    !product.planManagedInStripe; */

  // `initialValues` is derived from the async `seller` (AuthContext starts as
  // `{}`) and, when editing, the async `product`. Mounting the editable form
  // against that half-loaded data and relying on `enableReinitialize` to swap in
  // the real values once they land silently discarded anything the user touched
  // in that window — e.g. flipping the billing toggle right after a refresh
  // jumped back to one-time. So wait for the data, then mount once. We key the
  // form on the record identity (below) so switching products still reseeds, but
  // a background refetch of the same record no longer reinitializes over live
  // edits. After-save reseeds explicitly via resetForm in handleSubmit.
  const dataReady = Boolean(seller?._id) && (isNew || !isLoading);
  if (!dataReady) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  return (
    <Formik
      key={product?._id ?? 'new'}
      initialValues={initialValues}
      onSubmit={submitProduct}
      validationSchema={productSchema}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({
        status,
        handleSubmit,
        values,
        initialValues: formInitialValues,
        setFieldValue,
        setStatus,
        setErrors,
        setFieldError,
        resetForm,
      }) => {
        // Persist an explicit prices[] array instead of relying on submitForm(),
        // which reads Formik's committed values ref. A react-beautiful-dnd reorder
        // fires outside React's discrete-event flush, so an immediate submitForm()
        // would send the pre-reorder values (the ref only refreshes after the
        // setFieldValue re-render commits). Submitting the new array directly — the
        // same shape Formik's onSubmit receives — bypasses that stale read.
        const persistReorder = prices =>
          submitProduct(
            { ...values, prices },
            { setStatus, setErrors, setFieldError, resetForm, setFieldValue },
          );

        function handleApplyTemplate(name) {
          const result = applyTemplate(name, values.currency || 'usd');
          if (!result) return;
          if (!String(values.title || '').trim() && result.productTitleSuggestion) {
            setFieldValue('title', result.productTitleSuggestion);
          }
          setFieldValue('priceDisplayLabels', result.priceDisplayLabels ?? {});
          setFieldValue('priceDisplayDefaults', result.priceDisplayDefaults ?? {});
          setFieldValue('priceDisplayTabOrder', result.priceDisplayTabOrder ?? {});
          // Keep the create flow's always-visible "add another price" draft after a
          // template: committed template rows PLUS a trailing empty draft — the same
          // state you'd reach by adding those prices manually. Nulling the draft id
          // here would leave the committed rows with no visible price form.
          // Templates are a commit point like "Add another price"/the draft Save, so
          // run the same normalize + reference fill — otherwise the Reference field
          // stays blank until "Create checkout".
          const draftKey = uniqueId('price-');
          setFieldValue('prices', [
            ...fillMissingPriceReferences(
              result.prices.map(normalizeRevealFlags),
              values.currency || 'usd',
            ),
            { ...DEFAULT_NEW_PRICE, key: draftKey, currency: values.currency || 'usd' },
          ]);
          setFieldValue('defaultPriceId', result.prices[0].key);
          setFieldValue('__draftPriceId', draftKey);
        }

        // Single-price edit mode (mirrors PricesList): one committed price and no
        // open draft. The title is saved by that form's Save button (which submits
        // the whole form), so it must NOT autosave. Multi-price keeps autosave.
        const draftId = values.__draftPriceId ?? null;
        const priceKey = p => p._id || p.key;
        const committedCount = (values.prices || []).filter(p => priceKey(p) !== draftId).length;
        const hasOpenDraft =
          draftId != null && (values.prices || []).some(p => priceKey(p) === draftId);
        const isSinglePriceEdit = !isNew && committedCount === 1 && !hasOpenDraft;

        // TODO(pricing-type-revival): mode switch handlers for "priced with variants".
        const variantsPriced = isVariantsPriced(values);
        const switchToVariantsPricing = () => {
          const current = (values.prices || [])[0] || {};
          const next = toVariantsPricedPrice(current, values.currency || 'usd');
          setFieldValue('pricingType', 'multiple');
          setFieldValue('prices', [next]);
          if (isNew) setFieldValue('__draftPriceId', next.key);
        };
        const switchToBasePricing = () => {
          // Create only: clear the variants-zeroed amount so the Price field reads
          // empty (it still submits as 0); edit keeps the 0 for its Cancel/Save transition.
          if (isNew) {
            const prices = values.prices || [];
            const lone = prices[0];
            if (lone && String(lone.amount ?? '') === '0') {
              setFieldValue('prices', [{ ...lone, amount: '' }, ...prices.slice(1)]);
            }
          }
          setFieldValue('pricingType', 'single');
        };

        const pendingBaseSwitch = !isNew && !variantsPriced && isVariantsPriced(formInitialValues);
        const pendingVariantsSwitch =
          !isNew && variantsPriced && !isVariantsPriced(formInitialValues);
        // Pay-what-you-want has no base price to swap for variant pricing, so the
        // "Use variants instead" switch doesn't apply — hide it when the lone price
        // is PWYW (either billing model).
        const lonePriceIsPwyw = !!(values.prices || [])[0]?.payWhatYouWant;
        const showVariantsLink =
          !variantsPriced &&
          !pendingBaseSwitch &&
          !lonePriceIsPwyw &&
          (isNew ? (values.prices || []).length === 1 : isSinglePriceEdit);

        // The product title field. In single-price edit mode (normal or
        // variants-priced) it moves inside the boxed form and saves with the
        // Save button (no autosave); everywhere else it sits at the top.
        const titleField = (
          <div>
            <Label htmlFor="title">Product title</Label>
            <TitleAutosaveField isNew={isNew} autosave={!isSinglePriceEdit} />

            {!isNew && (
              <Tooltip label="Change the URL slug" side="bottom" align="start" asChild>
                {/* Link is a class component, so the anchor Radix measures is
                    this span rather than the link itself. */}
                <span className="mt-1 inline-flex">
                  <Link
                    to={`/pages/${page._id}/customize/settings/url-slug`}
                    className="inline-flex items-center gap-1 !font-normal text-xs !text-gray-500 dark:!text-muted-foreground underline hover:text-gray-700 dark:hover:text-muted-foreground"
                  >
                    <LinkIcon className="size-3 shrink-0" aria-hidden="true" />
                    <span>{page.slug}</span>
                  </Link>
                </span>
              </Tooltip>
            )}

            <FormError name="title" />
          </div>
        );

        return (
          <Form onSubmit={handleSubmit}>
            <UnsavedChangesReporter
              autosavedFields={!isNew && !isSinglePriceEdit ? ['title'] : []}
            />
            {/* {isNew && (
              <FormElement>
                <QuickStart onApply={handleApplyTemplate} />
              </FormElement>
            )} */}
            {!isSinglePriceEdit && <FormElement>{titleField}</FormElement>}

            {variantsPriced ? (
              <>
                <PricedWithVariantsSection
                  isNew={isNew}
                  titleField={isSinglePriceEdit ? titleField : undefined}
                  onCancelPricingSwitch={pendingVariantsSwitch ? () => resetForm() : undefined}
                />
                {/* Edit-only: switch back to a base price (create's link sits
                    below the CTA, mirroring the forward link). Hidden while a
                    base→variants switch is pending — the Cancel button in the
                    box owns that transition until it's saved. */}
                {!isNew && !pendingVariantsSwitch && (
                  <BasePricingLink onSwitch={switchToBasePricing} />
                )}
              </>
            ) : (
              <FormElement>
                <PricesList
                  startCollapsed={!isNew}
                  isNew={isNew}
                  onEditingChange={setEditingPriceId}
                  onPersistReorder={persistReorder}
                  variants={product?.variants}
                  editingSnapshotRef={editingSnapshotRef}
                  titleField={isSinglePriceEdit ? titleField : undefined}
                  onCancelPricingSwitch={pendingBaseSwitch ? () => resetForm() : undefined}
                  groupingAxis={
                    // Page overrides win over the base theme — same layering as
                    // the preview's mergeTheme, which also defaults an unstored
                    // value to 'auto' (the picker then derives the axis live).
                    page?.themeOverrides?.variables?.elements?.product?.pricePicker?.groupBy ??
                    page?.theme?.variables?.elements?.product?.pricePicker?.groupBy ??
                    'auto'
                  }
                />
                {!isNew && showVariantsLink && (
                  <VariantsPricingLink onSwitch={switchToVariantsPricing} />
                )}
              </FormElement>
            )}

            <FormErrorBase>{status && status}</FormErrorBase>

            {/* Edit autosaves (no Save button); create still confirms explicitly. */}
            {isNew && (
              <FormSubmit>
                <FormSubmit.Button
                  appearance="primary"
                  size="full"
                  button="shadcn"
                  // Larger 40px (size full → xl) full-width primary CTA; the
                  // "Add price" button above it stays the smaller 32px version.
                  className="w-full"
                  // Block submission while a committed price is expanded for editing
                  // (collapse/save it first). The always-visible draft form is not an
                  // "edit" — its id equals __draftPriceId.
                  disabled={editingPriceId != null && editingPriceId !== values.__draftPriceId}
                >
                  {variantsPriced ? 'Create checkout & add variants' : 'Create checkout'}
                </FormSubmit.Button>
              </FormSubmit>
            )}
            {isNew && showVariantsLink && (
              <VariantsPricingLink onSwitch={switchToVariantsPricing} />
            )}
            {isNew && variantsPriced && <BasePricingLink onSwitch={switchToBasePricing} />}
          </Form>
        );
      }}
    </Formik>
  );
};

export default ProductDetailsForm;
